import type { FunctionComponent } from "react";
import { useEffect, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import type { ArticleRenderChildProps } from "scmp-app/components/article/article-render";
import { useIntersection } from "scmp-app/lib/hooks";
import type { newsletterArticleArticle$key } from "scmp-app/queries/__generated__/newsletterArticleArticle.graphql";

import { DelayMillisecondsToHideNewsletterWidget } from "./consts";
import {
  AuthorDetail,
  Bottom,
  CommentTrigger,
  Container,
  Content,
  IframeContainer,
  NewsletterArticleWidgetContainer,
  Outer,
  ShareContainer,
  ShareWidget,
  SpeechPlayer,
  StyledNewsletterArticleContent,
  StyledNewsletterArticleHeader,
  StyledNewsletterArticleIssues,
  StyledNewsletterArticleWidget,
  StyledNewsletterArticleWidgetBottom,
  Topics,
} from "./styles";
import { Position } from "./widget";

export type Props = ArticleRenderChildProps & {
  reference: newsletterArticleArticle$key;
};

export const NewsletterArticle: FunctionComponent<Props> = ({ provider, reference }) => {
  const article = useFragment(
    graphql`
      fragment newsletterArticleArticle on Article {
        ...contentNewsletterArticleArticle
        ...headerNewsletterArticleArticle
        ...issuesNewsletterArticleArticle
        ...widgetNewsletterArticleArticle
      }
    `,
    reference,
  );

  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);

  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [isArticleNewsletterWidgetHidden, setIsArticleNewsletterWidgetHidden] = useState(false);
  const [isNewsletterSubscribed, setIsNewsletterSubscribed] = useState(false);

  const [isViewingNewsletterArticle, setIsViewingNewsletterArticle] = useState(false);
  const { captureIntersectionElement: startDetector, intersection: startIntersection } =
    useIntersection({});
  const { captureIntersectionElement: endDetector, intersection: endIntersection } =
    useIntersection({});

  useEffect(() => {
    if (!isMobile) return;

    const startIntersectionBoundTop = startIntersection?.boundingClientRect?.top ?? 1;
    const endIntersectionBoundTop = endIntersection?.boundingClientRect?.top ?? -1;

    const updatedIsViewingNewsletterArticle =
      !startIntersection?.isIntersecting &&
      startIntersectionBoundTop < 0 &&
      !endIntersection?.isIntersecting &&
      endIntersectionBoundTop > 0;
    setIsViewingNewsletterArticle(updatedIsViewingNewsletterArticle);
  }, [isMobile, startIntersection, endIntersection]);

  return (
    <Outer>
      <Container>
        <StyledNewsletterArticleHeader provider={provider} reference={article} />
        <SpeechPlayer>{provider.speechPlayer({})}</SpeechPlayer>
        <Content>
          <div ref={startDetector} />
          <NewsletterArticleWidgetContainer $isHidden={isArticleNewsletterWidgetHidden}>
            <StyledNewsletterArticleWidget
              isToggleSuccessMessage={isNewsletterSubscribed}
              onSubscribeSuccess={() => {
                setIsNewsletterSubscribed(true);
                setTimeout(() => {
                  setIsArticleNewsletterWidgetHidden(true);
                }, DelayMillisecondsToHideNewsletterWidget);
              }}
              position={Position.Sticky}
              reference={article}
            />
          </NewsletterArticleWidgetContainer>
          <IframeContainer>
            <StyledNewsletterArticleContent
              onIframeLoaded={() => {
                setIsIframeLoaded(true);
              }}
              reference={article}
            />
          </IframeContainer>
          <StyledNewsletterArticleIssues reference={article} />
          <div ref={endDetector} />
        </Content>
        {isIframeLoaded && (
          <Bottom>
            <ShareContainer>
              <CommentTrigger>{provider.commentTrigger()}</CommentTrigger>
              <ShareWidget>
                {provider.shareWidget({
                  variant: "legacyShare",
                })}
              </ShareWidget>
            </ShareContainer>
            <AuthorDetail>{provider.authorDetails({ firstNthAuthor: 1 })}</AuthorDetail>
            <Topics>{provider.topics()}</Topics>
          </Bottom>
        )}
      </Container>
      <StyledNewsletterArticleWidgetBottom
        $isHidden={!isViewingNewsletterArticle || isNewsletterSubscribed}
        onSubscribeSuccess={() => {
          setTimeout(
            () => setIsNewsletterSubscribed(true),
            DelayMillisecondsToHideNewsletterWidget,
          );
        }}
        position={Position.Bottom}
        reference={article}
      />
    </Outer>
  );
};

NewsletterArticle.displayName = "NewsletterArticle";
